/**
 * Triage Queue
 * AIDLC Admin Portal
 *
 * Open work that belongs to no sprint, which is how ad-hoc work reaches Sam.
 *
 * The dispatcher creates a backlog item for every chat and Go-Do-It run, and those
 * items join no sprint. They spend the budget regardless, so a sprint review that
 * never sees them understates what the sprint cost. This is where they surface,
 * with the baseline estimate each was given and whatever has already been spent on
 * it, because pulling something into a sprint is a budget decision as much as a
 * scope one.
 *
 * Nothing here decides for Sam. Items are listed, not auto-assigned.
 */

import { useCallback, useEffect, useState } from 'react';
import { getUnsprintedItems, type TriageQueue as TriageQueueData } from '../../services/backlogService';
import { fetchAllSprints, addItemsToSprint } from '../../services/sprintService';
import { getAllProjects } from '../../services/projectService';

// The shapes live in the service module beside the paths that produce them.
type SprintSummary = { id: string; name: string; status: string; projectId: string };

const money = (n: number | null): string => (n === null ? '—' : `$${n.toFixed(2)}`);

/** "1 item" / "2 items" - the header was reading "1 have already been worked
 *  on." and "1 carry no estimate." (Asha NEW-02). */
const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

export function TriageQueue() {
  const [data, setData] = useState<TriageQueueData | null>(null);
  const [sprints, setSprints] = useState<SprintSummary[]>([]);
  /** Project id to the name a person would recognise. */
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetSprint, setTargetSprint] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [triage, sprintList, projectList] = await Promise.all([
        getUnsprintedItems(),
        fetchAllSprints(),
        // Only to label the sprint options. A failure here must not empty the
        // queue, so it degrades to the project id rather than throwing.
        getAllProjects().catch(() => []),
      ]);
      setData(triage);
      setProjectNames(Object.fromEntries(projectList.map((p) => [p.id, p.name])));
      const open = sprintList.filter((s) => s.status === 'active' || s.status === 'planning');
      setSprints(open);
      setTargetSprint((prev) => prev || open.find((s) => s.status === 'active')?.id || open[0]?.id || '');
    } catch {
      setError('Could not read the triage queue.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addToSprint = async () => {
    if (!targetSprint || selected.size === 0 || busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await addItemsToSprint(targetSprint, [...selected]);
      const name = sprints.find((s) => s.id === targetSprint)?.name ?? 'the sprint';
      setDone(`${selected.size} ${selected.size === 1 ? 'item' : 'items'} added to ${name}.`);
      setSelected(new Set());
      await load();
    } catch {
      setError('Adding to the sprint failed. Nothing was moved.');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    // A failure and a load used to render the same grey sentence, so a queue that
    // could not be read looked like one that was still loading (Asha BUG-006).
    return (
      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        {error ? (
          <div role="alert" className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-700">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
            >
              Try again
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Reading the triage queue…</p>
        )}
      </section>
    );
  }

  const { items, totals } = data;

  return (
    <section className="mb-6 rounded-lg border border-amber-200 bg-amber-50/40 p-4" aria-labelledby="triage-h">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 id="triage-h" className="text-sm font-bold text-gray-900">
            Needs triage — {totals.count} {plural(totals.count, 'item', 'items')} in no sprint
          </h2>
          <p className="mt-1 text-xs text-gray-600">
            Created outside a sprint, mostly by the dispatcher answering chat and Go Do It. They spend the
            budget either way, so a sprint review that never sees them understates what the sprint cost.
            {totals.withoutEstimate > 0
              && ` ${totals.withoutEstimate} ${plural(totals.withoutEstimate, 'carries', 'carry')} no estimate.`}
            {totals.alreadySpentOn > 0
              && ` ${totals.alreadySpentOn} ${plural(totals.alreadySpentOn, 'has', 'have')} already been worked on.`}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs text-gray-600">
          <div>Estimated <span className="font-semibold text-gray-900">{money(totals.estimatedCost)}</span></div>
          <div>Spent so far <span className="font-semibold text-gray-900">{money(totals.actualCost)}</span></div>
        </div>
      </div>

      {error && <p role="alert" className="mt-2 text-xs text-red-700">{error}</p>}
      {done && <p role="status" className="mt-2 text-xs text-green-700">{done}</p>}

      {items.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500">
          Nothing is waiting. Every open item belongs to a sprint.
        </p>
      ) : (
        <>
          <div className="mt-3 max-h-80 overflow-y-auto rounded border border-gray-200 bg-white">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th scope="col" className="px-2 py-1.5 w-8"><span className="sr-only">Select</span></th>
                  <th scope="col" className="px-2 py-1.5">Item</th>
                  <th scope="col" className="px-2 py-1.5">Agent</th>
                  <th scope="col" className="px-2 py-1.5 text-right">Estimate</th>
                  <th scope="col" className="px-2 py-1.5 text-right">Spent</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        id={`triage-${i.id}`}
                        checked={selected.has(i.id)}
                        onChange={() => toggle(i.id)}
                        aria-label={`Select ${i.title}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <label htmlFor={`triage-${i.id}`} className="cursor-pointer text-gray-900">{i.title}</label>
                      <div className="text-[11px] text-gray-500">
                        {i.id} · {i.status} · {i.project} · filed by {i.reportedBy || 'unknown'}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">{i.assignedAgent ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {money(i.estimatedCost)}
                      {i.estimateConfidence && (
                        <div className="text-[11px] text-gray-500">{i.estimateConfidence} confidence</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {i.runs > 0 ? money(i.actualCost) : '—'}
                      {i.runs > 0 && <div className="text-[11px] text-gray-500">{i.runs} {plural(i.runs, 'run', 'runs')}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="triage-sprint" className="text-xs text-gray-600">Add selected to</label>
            {/* The project leads each option: open sprints from every project are
                listed, and the name alone does not say which is which. */}
            <select
              id="triage-sprint"
              value={targetSprint}
              onChange={(e) => setTargetSprint(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-xs"
            >
              {sprints.length === 0 && <option value="">No open sprint</option>}
              {sprints.map((s) => (
                <option key={s.id} value={s.id}>
                  {projectNames[s.projectId] ?? s.projectId} — {s.name}{s.status === 'active' ? ' (active)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void addToSprint()}
              disabled={busy || selected.size === 0 || !targetSprint}
              aria-describedby="triage-add-hint"
              className="rounded bg-ldaja-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Adding…' : `Add ${selected.size || ''} to sprint`}
            </button>
            {/* Named so the button can point at it, rather than it merely sitting
                alongside (Maya MAYA-12). */}
            <span id="triage-add-hint" className="text-[11px] text-gray-600">
              {sprints.length === 0
                // The button is disabled with nothing to add to, and said nothing
                // about why: the select-items hint only showed with no selection,
                // so a ticked row left a dead button unexplained (Asha BUG-004).
                ? 'No open sprint — create or activate one first.'
                : selected.size === 0 ? 'Select the items that belong in the sprint.' : ''}
            </span>
          </div>
        </>
      )}
    </section>
  );
}

export default TriageQueue;
